import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { fmtMemberId } from '@lib/member-id';
import { StageDots } from '@components/StageDots';
import { HeldHeight } from '@components/HeldHeight';
import { CareGrid, useGridRows } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { slicerCount, type SlicerDef } from '@lib/grid';
import { clientDisplayName } from '@lib/clients';
import { claimHolderName } from '@lib/claim-holder';
import { STAGED_WORDING, pendingCountOf } from '@lib/staged-presses';
import { MATCH_GROUP_WORDING, matchSortValue, threadMatchState, threadSuggestion } from '@lib/match-suggest';
import { MatchCell, SuggestionPill } from '@components/SuggestionPill';
import { ContactCell, LINE2_LEAD, MessageLine, ROWS_TABLE_CLASS, SuggestedCell, TwoLineCell, claimedByColumn, rowBand, rowNoColumn, tickColumn } from '@components/QueueRowCells';
import {
  CAMPAIGN_META,
  CHANNEL_META,
  REPLY_CLASS_META,
  STATUS_META,
  fmtPhone,
  SUGGESTED_META,
  fmtMinutes,
  fmtTs,
  liveTatMinutes,
  type EventRow,
  type ThreadRow,
} from '@lib/care-model';

/**
 * ThreadTable - one row per member / PCP / contact, instead of one row per event (William
 * 2026-07-09). Every inbound comm for the same person collapses into a single file; on the real
 * data that is 920 events over 619 threads, and the busiest thread holds 39.
 *
 * A THREAD IS A PROJECTION, NOT A RECORD. Expanding a row lists its events and clicking one opens
 * the SAME EventDrawer as the flat view. All per-event workflow (claim, disposition, send to review,
 * the resolution envelope) is untouched and still keyed on eventId. This table only groups.
 *
 * THE HIGHLIGHT (amber row + "New activity" pill) means: a `new` event landed on a thread that
 * already has at least one non-new event, so someone was working this person and something new
 * arrived. It is SHARED queue state, not per-operator unread: when anyone claims the new event, the
 * highlight clears for everyone. A thread whose events are ALL new is not highlighted - that is not
 * new activity on a known file, it is simply a new file, and the New tab already says so.
 *
 * ROW 356 (approval 169): renders through the shared grid (Columns, drag-to-resize, click and
 * Shift-click sorting, slicers, Expand, remembered per operator). A thread holding no campaign is
 * never hidden by a slicer (row 354), and whatever the slicers hide is counted on screen.
 */

/**
 * The fields the expanded event list reads: a queue row has them, and so does an archived row (row 400 rev2).
 * `pendingCount` (wave 3 item 4) is optional: an archived row and an older server's row carry none.
 */
export type ThreadEventLike = Pick<EventRow, 'eventId' | 'receivedTs' | 'channel' | 'subject' | 'preview' | 'status' | 'pendingCount'>;

/**
 * Wave 3 item 4: how many presses wait for approval across a thread's events (the list rows' own
 * pendingCount, summed). 0 on every thread while the deployment does not stage presses.
 */
/** PART N2: a thread's stop waiting for approval: any of its cases, and since the oldest. */
export function threadStop(t: Pick<ThreadRow, 'eventIds'>, eventsById: ReadonlyMap<string, Pick<EventRow, 'stopPending' | 'stopSince'>>): { stopPending: boolean; stopSince: string } {
  const since = t.eventIds.map((id) => eventsById.get(id)).filter((e) => e?.stopPending).map((e) => String(e?.stopSince ?? '')).filter(Boolean).sort();
  const any = t.eventIds.some((id) => eventsById.get(id)?.stopPending === true);
  return { stopPending: any, stopSince: since[0] ?? '' };
}

export function threadQueuedCount(t: Pick<ThreadRow, 'eventIds'>, eventsById: ReadonlyMap<string, Pick<EventRow, 'pendingCount'>>): number {
  return t.eventIds.reduce((n, id) => n + pendingCountOf(eventsById.get(id) ?? {}), 0);
}

/**
 * THE EXPANDED EVENT LIST of a thread: one button per event (time, channel, subject and preview, status),
 * clicking one opens it. Extracted for row 400 rev2 so the New Opt In grid's "other comms" row is the
 * general queue's own list, not a second one.
 */
export function ThreadEventList({
  events, selectedEventId, onSelectEvent,
}: {
  events: ReadonlyArray<ThreadEventLike>;
  selectedEventId: string | null;
  onSelectEvent: (id: string) => void;
}) {
  return (
    <>
      {events.map((ev) => {
        const chan = CHANNEL_META[ev.channel] ?? CHANNEL_META.Unknown;
        const evSt = STATUS_META[ev.status] ?? { label: ev.status, pill: '' };
        // Wave 3 item 4: the presses queued on this event, beside its status. With none (always so
        // while presses are not staged) the cell is the single status pill it always was.
        const queued = pendingCountOf(ev);
        return (
          <button
            key={ev.eventId}
            type="button"
            className="btn btn-ghost"
            onClick={() => onSelectEvent(ev.eventId)}
            data-testid={`thread-event-${ev.eventId}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '132px 92px 1fr 92px',
              gap: 10,
              width: '100%',
              textAlign: 'left',
              alignItems: 'center',
              padding: '5px 8px',
              font: 'inherit',
              background: selectedEventId === ev.eventId ? 'var(--paper-3)' : undefined,
            }}
          >
            <span className="small" style={{ whiteSpace: 'nowrap' }}>{fmtTs(ev.receivedTs)}</span>
            <span className={`pill ${chan.pill}`.trim()} style={{ justifySelf: 'start' }}>
              {chan.label}
            </span>
            <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ev.subject ? <b>{ev.subject} </b> : null}
              <span className="muted">{ev.preview}</span>
            </span>
            {queued > 0 ? (
              <span style={{ justifySelf: 'start' }}>
                <span className={`pill ${evSt.pill}`.trim()}>{evSt.label}</span>
                <span className="pill pill-warning" title={STAGED_WORDING.rowPillHelp(queued)} style={{ marginLeft: 4 }} data-testid={`thread-event-queued-${ev.eventId}`}>
                  {STAGED_WORDING.rowPill(queued)}
                </span>
              </span>
            ) : (
              <span className={`pill ${evSt.pill}`.trim()} style={{ justifySelf: 'start' }}>
                {evSt.label}
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}

export interface ThreadTableProps {
  threads: ThreadRow[];
  /** Every event row in the current scope, by id. Drives the expanded per-thread event list. */
  eventsById: ReadonlyMap<string, EventRow>;
  selectedEventId: string | null;
  loading: boolean;
  onSelectEvent: (eventId: string) => void;
  emptyLabel?: string;
  /** No longer read (16 Oct 2026 8:52 PM: the Claimed by column shows on every Status view); kept so callers compile. */
  showHandledBy?: boolean;
  /**
   * Slice B (his img7): open a member UNDER its row. When set, a row click chooses the thread's newest
   * entry (or closes it when the row is already open), and the row holding the chosen entry renders
   * renderBelow beneath it instead of the plain event list.
   */
  openBelow?: boolean;
  renderBelow?: (t: ThreadRow, selectedEventId: string) => ReactNode;
  onCloseBelow?: () => void;
  /** Where the slicers are drawn (the Inbound queue's right-side rail); absent = over the table. */
  slicerHost?: HTMLElement | null | undefined;
  /** The rail's Reset: each new value clears this table's slicers. */
  resetSlicersKey?: number | undefined;
  /** The rail's count: how many of this table's slicers have a value chosen. */
  onSlicerCount?: ((n: number) => void) | undefined;
  /**
   * 16 Oct 2026 (his word: "Like the print - put a checkmark next to each name"): a tick box on every
   * row for the queue's buttons on top. Absent = no tick column, as before.
   */
  selection?: ThreadSelection | undefined;
}

/** The tick boxes' state, held by the page (16 Oct 2026). Keys are thread keys. */
export interface ThreadSelection {
  checked: ReadonlySet<string>;
  onToggle: (threadKey: string) => void;
  /** The header box: tick every row on screen, or clear them all when every one is ticked. */
  onToggleAll: (shownKeys: string[]) => void;
  /** Told the rows on screen (after the slicers) whenever they change, so a hidden row can be unticked. */
  onShown?: ((shownKeys: string[]) => void) | undefined;
}

/** Non-member threads say what identity they DID key on, so "unmatched" is never a bare blank. */
const KIND_LABEL: Record<ThreadRow['kind'], string> = {
  member: '',
  pcp: 'PCP',
  phone: 'Unmatched phone',
  email: 'Unmatched email',
  event: 'No identity',
};

/** Pipeline order for the Stage column (the StageDots progression), so "Stage" sorts by how far
 * along the thread is rather than alphabetically. Unknown statuses sort to the middle. */
const STAGE_RANK: Record<string, number> = {
  new: 1,
  in_progress: 2,
  actioned: 3,
  in_review: 4,
  responded: 5,
  closed: 6,
  'closed-no-action': 6,
  cleared: 7,
  spam: 8,
};

const KIND_SLICER_LABEL: Record<ThreadRow['kind'], string> = {
  member: 'Member',
  pcp: 'PCP',
  phone: 'Unmatched phone',
  email: 'Unmatched email',
  event: 'No identity',
};

/**
 * Row 356: the slicers this view offers. Source and campaign stay the page's own chips (a thread
 * "holds" a source or a campaign), so these are the fields no chip filters. A thread holds a
 * channel the same way it holds a source.
 */
const THREAD_SLICERS: SlicerDef<ThreadRow>[] = [
  { key: 'kind', label: 'Who', value: (t) => t.kind, labelFor: (v) => KIND_SLICER_LABEL[v as ThreadRow['kind']] ?? v },
  { key: 'channels', label: 'Channel', value: (t) => t.channels, labelFor: (v) => CHANNEL_META[v as EventRow['channel']]?.label ?? v },
  // Slice B (his img7): the round a reply answered; a thread holds every cycle its replies answered.
  { key: 'cycle', label: 'Cycle', value: (t) => t.cycles ?? [], blankLabel: 'Not a text reply' },
  {
    key: 'suggested', label: 'Suggested action', value: (t) => t.suggestedAction, blankLabel: 'None',
    labelFor: (v) => (SUGGESTED_META as Record<string, { label: string }>)[v]?.label ?? v,
  },
  // No Status slicer here: the Inbound queue's own Status slicer in its rail (his img3, 24 Sep 2026)
  // narrows threads by their rollup status, and two "Status" groups would disagree.
  { key: 'newActivity', label: 'New activity', value: (t) => (t.hasNewActivity ? 'yes' : 'no'), labelFor: (v) => (v === 'yes' ? 'Yes' : 'No') },
  { key: 'handledBy', label: 'Claimed by', value: (t) => claimHolderName({ assignedTo: t.assignedTo, operatorName: t.operatorName }), blankLabel: 'Nobody' },
];

/** Row 354: a thread holding no campaign-bearing event is never hidden by a slicer. */
const noCampaign = (t: ThreadRow) => t.campaigns.length === 0;

const nowrap = { whiteSpace: 'nowrap' } as const;

/** The Campaign column's words for a thread: every campaign it holds, in the roster's own words. */
function campaignWords(t: ThreadRow): string {
  return t.campaigns.map((c) => CAMPAIGN_META[c]?.label ?? c).join(', ');
}

function threadTat(t: ThreadRow, nowMs: number): number | null {
  return t.tatMinutes ?? (t.oldestOpenTs ? liveTatMinutes(t.oldestOpenTs, undefined, nowMs) : null);
}

/**
 * The columns. First-click direction per column: text ascends (A->Z); counts, time and TAT descend
 * (most or newest first, which is what an operator usually wants on the first click). A blank sorts
 * last in either direction (it used to sort first on a descending click).
 */
/** The row's tick box state, as the columns read it (16 Oct 2026). */
interface TickState {
  checked: ReadonlySet<string>;
  onToggle: (threadKey: string) => void;
  onToggleAll: (threadKeys: string[]) => void;
  /** The rows on screen now (after the slicers), in screen order: what the header box ticks. */
  shownKeys: readonly string[];
}

/** The first line of a row's Member cell: the member's name, or the phone or address it keyed on. */
export function threadMainLine(t: Pick<ThreadRow, 'kind' | 'label'>): string {
  return t.kind === 'phone' ? fmtPhone(t.label) : t.label;
}

/**
 * The second line, under the name (his word, 16 Oct 2026: "each would be 2 lines with member id below"):
 * a member's id as the print screen shows it, the PCP's NPI, or what an unmatched row keyed on.
 */
export function threadSubLine(t: Pick<ThreadRow, 'kind' | 'sublabel' | 'matchConfidence'>): string {
  if (t.kind === 'member') return t.sublabel ? fmtMemberId(t.sublabel) : 'Member';
  if (t.kind === 'pcp') return t.sublabel;
  return t.matchConfidence === 'ambiguous' ? 'Ambiguous' : KIND_LABEL[t.kind];
}

/**
 * The Contact column (his word, 16 Oct 2026: "when an email comes in - then put the email address and
 * change the phone heading to say contact"): the phone the thread holds and the member's email address,
 * whichever it has, the phone first. An older server sends no contactEmail; an email row then shows the
 * address it keyed on.
 */
export function threadContact(t: Pick<ThreadRow, 'kind' | 'fromPhone' | 'fromAddress'> & { contactEmail?: string }): { phone: string; email: string } {
  const email = t.contactEmail || (t.kind === 'email' ? t.fromAddress : '');
  return { phone: t.fromPhone ? fmtPhone(t.fromPhone) : '', email };
}

/**
 * PART R (William, 17 Oct 2026): an unmatched row shows its suggestion pill (or the match waiting for approval) in
 * place of "Unmatched phone/email"; every other row its second line as before.
 */
export function threadSubNode(t: ThreadRow, eventsById: ReadonlyMap<string, EventRow>): ReactNode {
  if (t.kind !== 'member' && t.kind !== 'pcp') {
    const hit = threadSuggestion(t, eventsById);
    if (hit && (hit.matchPending || hit.suggestion)) {
      return <SuggestionPill suggestion={hit.suggestion} pending={hit.matchPending} testId={`thread-suggest-${t.threadKey}`} />;
    }
  }
  return threadSubLine(t);
}

function threadColumns(nowMs: number, expanded: ReadonlySet<string>, eventsById: ReadonlyMap<string, EventRow>, tick: TickState | null): GridColumn<ThreadRow>[] {
  const allShown = !!tick && tick.shownKeys.length > 0 && tick.shownKeys.every((k) => tick.checked.has(k));
  return [
    // 16 Oct 2026 (his word: "Like the print - put a checkmark next to each name"). The header box ticks
    // every row on screen, or clears them all. Omitted where the table is not given a selection.
    tickColumn<ThreadRow>({
      on: !!tick,
      allShown,
      onToggleAll: () => tick?.onToggleAll([...tick.shownKeys]),
      isChecked: (t) => tick?.checked.has(t.threadKey) ?? false,
      onToggle: (t) => tick?.onToggle(t.threadKey),
      ariaLabel: (t) => `Tick ${t.label}`,
      testId: (t) => `thread-tick-${t.threadKey}`,
    }),
    rowNoColumn<ThreadRow>(),
    {
      key: 'open', label: '', menuLabel: 'Open marker', locked: true,
      thStyle: { width: 30 },
      tdStyle: { textAlign: 'center', fontSize: '1.7em', lineHeight: 1, color: 'var(--ink-2)' },
      render: (t) => (t.eventCount > 1 ? (expanded.has(t.threadKey) ? '▾' : '▸') : ''),
    },
    {
      key: 'member', label: 'Member / Contact', tdStyle: nowrap, sortValue: (t) => t.label,
      // The name and the id (or what the row keyed on); the suggestion pill moved to Match (17 Oct 2026).
      render: (t) => (
        <TwoLineCell
          main={threadMainLine(t)}
          sub={threadSubLine(t)}
          subTestId={`thread-sub-${t.threadKey}`}
          extra={t.hasNewActivity && (
            <span className="pill pill-warning" style={{ marginLeft: 6 }} title={`${t.newCount} new since this thread was last worked`}>
              New activity
            </span>
          )}
        />
      ),
    },
    {
      // William, 17 Oct 2026 about 12:50 AM: "the matched flag in its own col. sortable".
      key: 'match', label: MATCH_GROUP_WORDING.column, tdStyle: nowrap, sortValue: (t) => matchSortValue(threadMatchState(t, eventsById)),
      render: (t) => (
        <MatchCell state={threadMatchState(t, eventsById)} testId={`thread-match-${t.threadKey}`} pillTestId={`thread-suggest-${t.threadKey}`} />
      ),
    },
    // Slice B (his img7, 24 Sep 2026): "Add more columns, including Client, Campaign, Cycle. And other
    // reasonable columns." Each sorts (text ascends on the first click) and hides from Columns.
    {
      key: 'client', label: 'Client', tdStyle: nowrap, sortValue: (t) => (t.client ? clientDisplayName(t.client) : ''),
      render: (t) => (t.client ? clientDisplayName(t.client) : <span className="muted">-</span>),
    },
    {
      key: 'campaign', label: 'Campaign', tdStyle: nowrap, sortValue: (t) => campaignWords(t),
      render: (t) =>
        t.campaigns.length ? (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {t.campaigns.map((c) => (
              <span key={c} className={`pill ${CAMPAIGN_META[c]?.pill ?? ''}`.trim()}>{CAMPAIGN_META[c]?.label ?? c}</span>
            ))}
          </span>
        ) : (
          <span className="muted">No campaign</span>
        ),
    },
    {
      key: 'cycle', label: 'Cycle', tdStyle: nowrap, sortValue: (t) => (t.cycles ?? []).join(', '),
      title: 'The round of the campaign each text reply answered (Opt-in 01, Opt-in 02)',
      render: (t) => ((t.cycles ?? []).length ? (t.cycles ?? []).join(', ') : <span className="muted">-</span>),
    },
    {
      key: 'cohort', label: 'Cohort', tdStyle: nowrap, sortValue: (t) => t.cohort ?? '',
      title: "The member's cohort, in the campaign roster's own words",
      render: (t) => t.cohort || <span className="muted">-</span>,
    },
    // His word, 16 Oct 2026: the Offer column is dropped from this table.
    {
      key: 'lastReply', label: 'Text reply', tdStyle: nowrap,
      sortValue: (t) => (t.lastReplyClass ? REPLY_CLASS_META[t.lastReplyClass as keyof typeof REPLY_CLASS_META]?.label ?? t.lastReplyClass : ''),
      title: "The platform's reading of the member's newest text reply",
      render: (t) => {
        const meta = t.lastReplyClass ? REPLY_CLASS_META[t.lastReplyClass as keyof typeof REPLY_CLASS_META] : null;
        if (!t.lastReplyClass) return <span className="muted">-</span>;
        return <span className={`pill ${meta?.pill ?? ''}`.trim()} title={meta?.help}>{meta?.label ?? t.lastReplyClass}</span>;
      },
    },
    {
      // Key kept as 'phone' so an operator's remembered width and place still apply; the heading is Contact.
      key: 'phone', label: 'Contact', tdStyle: nowrap,
      sortValue: (t) => { const c = threadContact(t); return c.phone || c.email; },
      render: (t) => {
        const c = threadContact(t);
        return <ContactCell phone={c.phone} email={c.email} />;
      },
    },
    {
      key: 'channels', label: 'Channels', sortValue: (t) => t.channels.join(','),
      render: (t) => (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {t.channels.map((c) => {
            const meta = CHANNEL_META[c] ?? CHANNEL_META.Unknown;
            return (
              <span key={c} className={`pill ${meta.pill}`.trim()}>
                {meta.label}
              </span>
            );
          })}
          {t.hasAudio ? <span title="audio"> ♪</span> : null}
        </span>
      ),
    },
    {
      key: 'events', label: 'Events', tdStyle: nowrap, firstDir: 'desc', sortValue: (t) => t.eventCount,
      render: (t) => (
        <>
          {t.eventCount}
          {t.newCount > 0 && <span className="micro muted"> ({t.newCount} new)</span>}
        </>
      ),
    },
    {
      key: 'suggested', label: 'Suggested action', tdStyle: nowrap,
      sortValue: (t) => (t.suggestedAction ? SUGGESTED_META[t.suggestedAction]?.label ?? t.suggestedAction : ''),
      // PART N2: the thread shows "Stop pending approval" when any of its cases has a stop waiting (the oldest).
      render: (t) => <SuggestedCell r={{ ...t, ...threadStop(t, eventsById) }} stopWords="the STOP in this thread" />,
    },
    {
      key: 'lastActivity', label: 'Last activity', tdStyle: nowrap, firstDir: 'desc',
      sortValue: (t) => Date.parse(t.lastTs) || null, render: (t) => fmtTs(t.lastTs),
    },
    { key: 'stage', label: 'Stage', sortValue: (t) => STAGE_RANK[t.status] ?? 50, render: (t) => <StageDots status={t.status} /> },
    {
      key: 'status', label: 'Status', sortValue: (t) => STATUS_META[t.status]?.label ?? t.status,
      render: (t) => {
        const st = STATUS_META[t.status] ?? { label: t.status, pill: '' };
        // Wave 3 item 4: the presses queued across the thread's events (their list rows' pendingCount).
        // Shown only when some are, which is never while presses are not staged.
        const queued = threadQueuedCount(t, eventsById);
        return (
          <>
            <span className={`pill ${st.pill}`.trim()}>{st.label}</span>
            {queued > 0 && (
              <span className="pill pill-warning" title={STAGED_WORDING.rowPillHelp(queued)} style={{ marginLeft: 4 }} data-testid={`thread-queued-${t.threadKey}`}>
                {STAGED_WORDING.rowPill(queued)}
              </span>
            )}
            {t.followUp ? <span title="follow-up"> ⏰</span> : null}
            {t.hasNotes ? <span title="has notes"> ✎</span> : null}
          </>
        );
      },
    },
    {
      key: 'tat', label: 'TAT', tdClassName: 'small', tdStyle: nowrap, firstDir: 'desc', sortValue: (t) => threadTat(t, nowMs),
      render: (t) => {
        // Frozen once every event in the thread is terminal; otherwise tick from the OLDEST open
        // event, because a thread is not resolved until its slowest event is.
        const mins = threadTat(t, nowMs);
        const open = t.tatMinutes == null;
        return (
          <span title={open ? 'open: oldest unresolved event to now' : 'receipt to resolution'}>
            {mins === null ? <span className="muted">-</span> : <span className={open ? 'muted' : undefined}>{fmtMinutes(mins)}</span>}
          </span>
        );
      },
    },
    // 16 Oct 2026 8:52 PM: who holds the member's row, from its newest claimed entry (the entries ride in
    // the same response); a dash when none of its entries is claimed.
    claimedByColumn<ThreadRow>((t) => t.eventIds.map((id) => eventsById.get(id)).find((e) => !!e?.assignedTo)),
  ];
}

export function ThreadTable({
  threads,
  eventsById,
  selectedEventId,
  loading,
  onSelectEvent,
  emptyLabel = 'No threads in this view.',
  openBelow = false,
  renderBelow,
  onCloseBelow,
  slicerHost,
  resetSlicersKey,
  onSlicerCount,
  selection,
}: ThreadTableProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  // The rows on screen (after the slicers), in screen order, for the header tick box and the page. Kept
  // as state because the columns are built before the view that decides which rows show.
  const [shownKeys, setShownKeys] = useState<readonly string[]>([]);
  // Tick the open-thread TAT once a minute so live rows advance between queue polls.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Sort (William 2026-07-10): every column. Default = Last activity, newest first, which matches
  // the server's existing order so the first render is unchanged.
  // The events ride along (wave 3 item 4) so the Status column can count the presses queued on a thread.
  const tick = useMemo<TickState | null>(
    () => (selection ? { checked: selection.checked, onToggle: selection.onToggle, onToggleAll: selection.onToggleAll, shownKeys } : null),
    [selection, shownKeys],
  );
  const columns = useMemo(() => threadColumns(nowMs, expanded, eventsById, tick), [nowMs, expanded, eventsById, tick]);
  const view = useGridView('inbound-threads', columns, {
    defaultSort: [{ k: 'lastActivity', asc: false }],
    slicers: THREAD_SLICERS,
    // The Claimed by column shows on every Status view (16 Oct 2026 8:52 PM); showHandledBy is no longer read here.
    omit: selection ? [] : ['select'],
  });

  // The same rows, in the same order, the grid shows (it sorts and slices with this one function).
  const shown = useGridRows(view, threads, noCampaign).rows;
  const shownJoined = shown.map((t) => t.threadKey).join('\n');
  const onShown = selection?.onShown;
  useEffect(() => {
    const keys = shownJoined ? shownJoined.split('\n') : [];
    setShownKeys((prev) => (prev.join('\n') === shownJoined ? prev : keys));
    onShown?.(keys);
  }, [shownJoined, onShown]);

  // The Inbound queue's rail (his img6): its Reset clears these slicers too, and its count includes them.
  const activeSlicers = slicerCount(view.slicers);
  useEffect(() => {
    onSlicerCount?.(activeSlicers);
  }, [activeSlicers, onSlicerCount]);
  const clearSlicers = view.clearSlicers;
  const firstReset = useRef(resetSlicersKey);
  useEffect(() => {
    if (resetSlicersKey === firstReset.current) return;
    firstReset.current = resetSlicersKey;
    clearSlicers();
  }, [resetSlicersKey, clearSlicers]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  /** A single-event thread has nothing to expand into: open the event directly. */
  const holds = (t: ThreadRow) => selectedEventId != null && t.eventIds.includes(selectedEventId);
  const below = openBelow && !!renderBelow;
  const activate = (t: ThreadRow) => {
    if (below) {
      if (holds(t)) onCloseBelow?.();
      else onSelectEvent(t.latestEventId);
      return;
    }
    if (t.eventCount === 1) onSelectEvent(t.latestEventId);
    else toggle(t.threadKey);
  };

  return (
    <HeldHeight hold={loading}>
      <CareGrid
        view={view}
        rows={threads}
        rowKey={(t) => t.threadKey}
        noun="threads"
        protect={noCampaign}
        protectWhat="Contacts with no campaign"
        tableWrapClassName="card card-flush"
        className={ROWS_TABLE_CLASS}
        empty={loading ? 'Loading...' : emptyLabel}
        slicerHost={slicerHost}
        // William, 16 Oct 2026 8:30 PM: the newest message's words under each row, up to two lines. The
        // thread's events are newest first and ride in the same response, so nothing more is read.
        line2={{
          lead: LINE2_LEAD,
          render: (t) => {
            const newest = eventsById.get(t.latestEventId) ?? eventsById.get(t.eventIds[0] ?? '');
            return newest ? <MessageLine r={newest} /> : null;
          },
        }}
        rowProps={(t, i) => {
          const st = STATUS_META[t.status] ?? { label: t.status, pill: '' };
          const isOpen = expanded.has(t.threadKey);
          const holdsSelected = selectedEventId != null && t.eventIds.includes(selectedEventId);
          // 16 Oct 2026, like the print screen: alternate rows banded, unless the row has its own colour.
          const band = rowBand(i);
          const background = t.hasNewActivity ? 'var(--amber-bg)' : holdsSelected ? 'var(--paper-2)' : band;
          return {
            tabIndex: 0,
            role: 'button',
            'aria-expanded': below ? holds(t) : t.eventCount > 1 ? isOpen : undefined,
            'aria-label': `${t.label}, ${t.eventCount} event${t.eventCount === 1 ? '' : 's'}, ${st.label}${t.hasNewActivity ? ', new activity' : ''}`,
            onClick: () => activate(t),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate(t);
              }
            },
            style: {
              cursor: 'pointer',
              background,
              boxShadow: t.hasNewActivity ? 'inset 3px 0 0 var(--amber)' : undefined,
            },
          };
        }}
        after={(t) =>
          below && holds(t) ? (
            renderBelow!(t, selectedEventId!)
          ) : !below && expanded.has(t.threadKey) ? (
            <div style={{ padding: '6px 12px 10px 34px', background: 'var(--paper-2)' }}>
              <ThreadEventList
                events={t.eventIds.map((id) => eventsById.get(id)).filter((ev): ev is EventRow => !!ev)}
                selectedEventId={selectedEventId}
                onSelectEvent={onSelectEvent}
              />
            </div>
          ) : null
        }
      />
    </HeldHeight>
  );
}
